<template>
    <div>
        <div class="row mb-3">
            <div class="col-auto" v-if="journey">
                <a class="btn btn-outline-secondary" :href="sankeyUrl">
                    <i class="fas fa-project-diagram"></i> {{resource('JourneyComposer_Overview_JourneyReport_Button')}}
                </a>
            </div>
            <div class="col-auto">
                <a class="btn btn-outline-secondary" href="/JourneyBuilder/Overview">
                    <i class="fas fa-route"></i> {{resource('JourneyBuilder_Index_Overview_Button_Text')}}
                </a>
            </div>
        </div>
        <bootstrap-box>
            <template v-slot:body v-if="journey">
                <div class="row">
                    <div class="col-3 menu-container" :class="{ 'collapsed': !showMenu }">
                        <toggle-button v-model="showMenu"
                                       :sync="true"
                                       :labels="{checked: resource('Bool_Expand'), unchecked: resource('Bool_Collapse')}"
                                       :width="78"

                                       class="menu-toggle"
                        >

                        </toggle-button>

                        <div class="menu-wrapper">
                            <journey-builder-menu-area
                                    :journey-id="journey.id"
                                    :journey-name.sync="journey.name"
                                    :journey-world.sync="journey.worldId"
                                    :journey-description.sync="journey.description"
                                    :journey-start-date.sync="journey.startDate"
                                    :journey-end-date.sync="journey.endDate"
                                    :journey-is-active.sync="journey.isActive"
                                    :journey-division-id.sync="journey.divisionId"

                                    v-on:update:journeyStartDate="setStartDate($event)"
                                    v-on:update:journeyEndDate="setEndDate($event)"

                                    @save="publishJourney"
                                    @clone="cloneJourney(journey.id)"
                                    @add-brick="addBrickToJourney"
                            >
                            </journey-builder-menu-area>
                        </div>
                    </div>

                    <div class="col">
                        <journey-builder-composer
                                ref="composer"

                                :available-conditions="availableConditions"
                                :bricks="journey.bricks"

                                @top-left-changed="setNewBrickDefaultPosition($event.x, $event.y)"

                                @brick-moved="brickMoved($event.brickId, $event.x, $event.y)"
                                @connector-moved="connectorMoved($event.connectionId, $event.sourceBrickId, $event.connectorX, $event.connectorY)"

                                @add-connection="addConnectionToBrick($event.id)"
                                @delete="deleteBrick($event.brick.id, $event.requiresPrompt)"
                                @settings-changed="updateSettings($event.id, $event.config, $event.name)"
                                @connection-created="connectBricks($event.connectionId, $event.sourceBrickId, $event.destinationBrickId)"
                                @change-connection="changeConnection($event.sourceBrickId, $event.model)"
                                @delete-connection="deleteConnection($event.sourceBrickId, $event.destinationBrickId, $event.connectionId)"
                        >
                        </journey-builder-composer>

                        <!--
                        TODO: handle event? 
                        @connection-active="setConnectorActive($event.connectionId, $event.sourceBrickId, $event.active)"
                        -->
                    </div>
                </div>
            </template>
        </bootstrap-box>
    </div>
</template>

<script>
    import api from '../api';
    import createJourneyBrickFactory from '../Bricks/journey-brick-factory';
    
    import {
        bootstrapDangerGrowl as errorGrowl,
        bootstrapSuccessGrowl as successGrowl
    } from "../../../Util/growler";

    import snowplow from '../snowplow';
    import { brickDefinitions } from '../Bricks/brick-definitions';
    import {promptV2 as prompt} from "../../../Util/prompt";
    import {sprintf} from 'sprintf'
    import {getCurrentUser} from "../../../Util/current-user";

    import resourceMixin from "../../../Util/Mixins/resources";
    import cloneJourneyMixin from '../Mixins/clone-journey';
    import permissionsMixin from '../Mixins/journey-permissions';
    
    import BootstrapBox from "../../../Components/Util/BootstrapBox.vue";
    import JourneyBuilderMenuArea from './Menu/JourneyBuilderMenuArea.vue';
    import JourneyBuilderComposer from "./Composer/JourneyBuilderComposer.vue";
    import JourneyBuilderComposerTouchpointOverview from "./Composer/JourneyBuilderComposerTouchpointOverview.vue";
    import { ToggleButton } from 'vue-js-toggle-button'
    
    export default {
        name: 'index',
        components: {
            BootstrapBox,
            JourneyBuilderMenuArea,
            JourneyBuilderComposer,
            JourneyBuilderComposerTouchpointOverview,
            ToggleButton
        },
        mixins: [resourceMixin, cloneJourneyMixin, permissionsMixin],
        
        created() {
            const vm = this;
            
            //An empty journey provides a definition for our properties/ensures the UI does not crash
            let getJourneyProm = new Promise((resolve) => resolve({
                id: null,
                name: '',
                worldId: '',
                description: '',
                isActive: true,
                bricks: [],
                divisionId: getCurrentUser().divisionId
            }));

            const getConditionsProm = api.getJourneyConditions();
            
            if(vm.id){
                getJourneyProm = api.getJourneyById(vm.id);
            }
            
            api.getJourneyBrickDefinitions().then(
                brickDefs => {
                    const typeConfigMapping = brickDefs.reduce((map, brickDef) => {
                        map[brickDef.type] = JSON.parse(brickDef.config);
                        return map;
                    }, {});

                    getConditionsProm.then(
                        conditions => {
                            vm.availableConditions = conditions;
                            vm.journeyBrickFactory = createJourneyBrickFactory(typeConfigMapping);

                            //A brickFactory is required before we can initialise our journey
                            getJourneyProm.then(
                                journey => vm.setJourney(journey),
                                err => errorGrowl(err)
                            )
                        },
                        err => errorGrowl(err)
                    );
                },
                err => errorGrowl(err)
            );
        },
        
        methods: {
            setStartDate(date){
                this.journey.startDate = date;
            },
            setEndDate(date){
                this.journey.endDate = date;
            },
            setJourney(journey){
                const vm = this;
                
                //We should normally not invoke mixin privates directly
                vm.$_setLockedState(true);
                
                const brickModels = journey.bricks;
                
                //Explicit brick creation gives us more flexibility regarding funcs/properties
                journey.bricks = brickModels.map(brickModel => vm.journeyBrickFactory.createJourneyBrickFromModel(brickModel));
                
                const brickMap = journey.bricks.reduce((brickMap, brick) => {
                    brickMap[brick.id] = brick;
                    return brickMap;
                }, {});
                
                // Once all bricks have been created; we can start completing our connections
                brickModels.forEach(brickModel => {
                    const brick = brickMap[brickModel.id];

                    brick.outgoingConnections.forEach(connection => {
                        const modelConnection = brickModel.connections.find(c => c.id === connection.id);
                        if(modelConnection && modelConnection.destinationBrickId){
                            connection.destinationBrick = brickMap[modelConnection.destinationBrickId];
                        }
                    });
                });

                vm.journey = journey;
            },
            getPublishJourneyPrompt(){
                const vm = this;
                
                //An inactive journey does not need a prompt prior to saving
                if(!vm.journey.isActive){
                    return Promise.resolve();
                }

                const promptText = vm.journey.startDate ?
                    sprintf(vm.resource('JourneyBuilder_Index_PublishJourney_WithStartDate_ConfirmationBody'), vm.journey.startDate) :
                    vm.resource('JourneyBuilder_Index_PublishJourney_WithoutStartDate_ConfirmationBody');
                
                return prompt(vm.resource('JourneyBuilder_Index_PublishJourney_ConfirmationTitle'), promptText);
            },
            publishJourney(){
                const vm = this;
                const isNewJourney = vm.journey.id === null;
                
                snowplow.trackStructuredEvent('publish-journey', vm.journey.id);

                vm.getPublishJourneyPrompt().then(() => {
                    api.publishJourney(vm.journey).then(
                        journey => {
                            successGrowl(vm.resource('JourneyBuilder_Index_JourneyPublished_SuccessMessage'));

                            //Upon creation of a new journey; we want to reflect the newly created journey in our URL
                            //this makes for easier sharing of new journeys/no accidental loss of current journey upon page reload
                            if(isNewJourney){
                                //We only need the id of our newly created journey, everything else can remain as is
                                //this approach also ensures we don't lose any potential unconnected endpoints
                                vm.journey.id = journey.id;
                                vm.reflectCurrentJourneyInBrowserUrl();
                            }
                            
                            //Upon saving; all bricks can be considered not-new, enabling a new set of features for the given brick
                            vm.journey.bricks.forEach(brick => brick.isNew = false);
                        },
                        err => errorGrowl(err)
                    )},
                    () => {}
                )
            },
            setNewBrickDefaultPosition(x, y){
                this.brickDefaultX = x;
                this.brickDefaultY = y;
            },
            addBrickToJourney(brickDefinition){
                snowplow.trackStructuredEvent('add-brick', brickDefinition.type);
                
                const brick = this.journeyBrickFactory.createNewJourneyBrick(brickDefinition.type);
                
                //Add 15px to ensure endpoint visibility
                brick.x = this.brickDefaultX + 15;
                brick.y = this.brickDefaultY + 15;
                
                this.journey.bricks.push(brick);
            },
            addConnectionToBrick(brickId){
                snowplow.trackStructuredEvent('add-connection', brickId);
                
                const brick = this.journey.bricks.find(b => b.id === brickId);
                brick.addConnection();
            },
            deleteBrick(brickId, requiresPrompt) {
                snowplow.trackStructuredEvent('delete-brick', brickId);

                const vm = this;
                
                let promptmise = Promise.resolve();
                if(requiresPrompt){
                    promptmise = prompt(
                        vm.resource('JourneyBuilder_Index_DeleteBrick_ConfirmationTitle'),
                        vm.resource('JourneyBuilder_Index_DeleteBrick_ConfirmationBody')
                    );
                }

                promptmise.then(() => {
                    const brick = vm.journey.bricks.find(b => b.id === brickId);

                    //Create a copy as to not cause crashes/weird behavior due to the connections
                    //being edited in the deleteConnection(...) function 
                    [...brick.connections].forEach(connection => connection.delete());

                    vm.journey.bricks.splice(vm.journey.bricks.indexOf(brick), 1);
                }, () => {});
            },
            updateSettings(brickId, config, name) {
                const brick = this.journey.bricks.find(b => b.id === brickId);
                
                brick.config = config || {};
                brick.name = name || brickDefinitions[brick.type].name;
            },
            connectBricks(connectionId, sourceBrickId, destinationBrickId){
                snowplow.trackStructuredEvent('connect-bricks', connectionId, JSON.stringify({sourceBrickId, destinationBrickId}));

                const sourceBrick = this.journey.bricks.find(b => b.id === sourceBrickId);
                const destinationBrick = this.journey.bricks.find(b => b.id === destinationBrickId);
                
                const connection = sourceBrick.connections.find(r => r.id === connectionId);
                connection.destinationBrick = destinationBrick;

                //Instead of starting out all the way at the top;
                //place our connector in the approximately the middle of the bricks it's connected to
                connection.connectorX = (sourceBrick.x + destinationBrick.x) / 2;
                connection.connectorY = (sourceBrick.y + destinationBrick.y) / 2;
            },
            brickMoved(brickId, x, y){
                const brick = this.journey.bricks.find(b => b.id === brickId);
                
                brick.x = x;
                brick.y = y;
            },
            connectorMoved(connectionId, sourceBrickId, connectorX, connectorY){
                const brick = this.journey.bricks.find(b => b.id === sourceBrickId);
                const connection = brick.connections.find(c => c.id === connectionId);
                
                connection.connectorX = connectorX;
                connection.connectorY = connectorY;
            },
            changeConnection(sourceBrickId, model) {
                snowplow.trackStructuredEvent('change-connection', model.id);

                const brick = this.journey.bricks.find(b => b.id === sourceBrickId);
                const connection = brick.connections.find(c => c.id === model.id);
                
                connection.id = model.id;
                connection.name = model.name;
                connection.isDisabled = model.isDisabled;
                connection.startDate = model.startDate;
                connection.conditions = model.conditions;
                connection.config = model.config;
            },
            deleteConnection(sourceBrickId, destinationBrickId, connectionId) {
                snowplow.trackStructuredEvent('delete-connection', connectionId, JSON.stringify({sourceBrickId, destinationBrickId}));
                
                const source = this.journey.bricks.find(b => b.id === sourceBrickId);
                const connection = source.connections.find(c => c.id === connectionId);

                connection.delete();
            },
            reflectCurrentJourneyInBrowserUrl(){
                const vm = this;
                
                if(window.history && window.history.pushState){
                    window.history.pushState(
                        null,
                        document.title,
                        api.getViewJourneyUrl(vm.journey.id)
                    )
                }
            }
        },
        computed: {
            sankeyUrl() {
                const vm = this;
                return api.getStatisticsPageUrl(vm.journey.id);
            }
        },
        data(){
            return {
                showMenu: true,
                
                journey: null,         
                //Object definition; overwritten in Create(...) handler
                journeyBrickFactory: {
                    createJourneyBrick: (type) => null,
                    createJourneyBrickFromModel: (remoteModel) => null
                },
                brickDefaultX: 0,
                brickDefaultY: 0,
                availableConditions: []
            }
        },
        props: {
            id: {
                required: false,
                type: String
            }
        }
    }
</script>

<style scoped lang="scss" src="../Style/journey-builder-index.scss"/>
